/**
 * New work plan year (operator order 2026-08-29): carry the same activities
 * year over year, or tick only the ones to carry, then add new ones on the
 * new plan with Add activity or Build annual work plan.
 *
 * One line per ACTIVITY, never per month: a synced measure carries as its
 * series (the new year gets its 12 / 4 / 2 rows at once); a hand-entered
 * activity carries as a copy of its definition, fanned out across the year.
 * Period results never carry. Expired work never carries.
 */
import React, { useEffect, useMemo, useState } from 'react';

export default function RolloverDialog({ api, scopeQ = '', workPlans = [], onClose, onCreated }) {
  const sorted = useMemo(() => [...workPlans].sort((a, b) => Number(b.plan_year) - Number(a.plan_year)), [workPlans]);
  const latest = sorted[0] || null;
  const defaultYear = latest ? Number(latest.plan_year) + 1 : new Date().getFullYear();
  const [year, setYear] = useState(defaultYear);
  const [sourceId, setSourceId] = useState(latest?.id || '');
  const [cands, setCands] = useState({ series: [], manual: [] });
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const source = workPlans.find((p) => p.id === sourceId) || null;
  const sameYear = source && Number(source.plan_year) === Number(year);

  useEffect(() => {
    if (!sourceId) { setCands({ series: [], manual: [] }); setPicked(new Set()); return; }
    setLoading(true); setError('');
    fetch(`${api}/api/coa/work-plans/${sourceId}/rollover-candidates${scopeQ}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        setLoading(false);
        if (!d.success) { setError(d.message || 'Could not read the activities of that year.'); return; }
        const series = d.series || [];
        const manual = d.manual || [];
        setCands({ series, manual });
        setPicked(new Set([...series.map((s) => `s:${s.id}`), ...manual.map((m) => `m:${m.id}`)]));
      })
      .catch(() => { setLoading(false); setError('Could not read the activities of that year.'); });
  }, [api, scopeQ, sourceId]);

  const total = cands.series.length + cands.manual.length;
  const toggle = (key) => setPicked((prev) => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const all = () => setPicked(new Set([...cands.series.map((s) => `s:${s.id}`), ...cands.manual.map((m) => `m:${m.id}`)]));
  const none = () => setPicked(new Set());

  const submit = (carry) => {
    if (busy) return;
    setBusy(true); setError('');
    const series_ids = [...picked].filter((k) => k.startsWith('s:')).map((k) => k.slice(2));
    const manual_ids = [...picked].filter((k) => k.startsWith('m:')).map((k) => k.slice(2));
    const body = carry && source
      ? { from_work_plan_id: source.id, plan_year: Number(year), all: picked.size === total, series_ids, manual_ids }
      : { plan_year: Number(year) };
    fetch(`${api}/api/coa/work-plans/rollover${scopeQ}`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    })
      .then((r) => r.json())
      .then((d) => {
        setBusy(false);
        if (!d.success) { setError(d.message || 'Failed'); return; }
        onCreated(d);
      })
      .catch((e) => { setBusy(false); setError(e.message); });
  };

  const cadenceWord = (c) => ({ monthly: 'Monthly · 12', quarterly: 'Quarterly · 4', semiannual: 'Semiannual · 2', annual: 'Annual · 1' }[c] || c);
  const freqWord = (f) => {
    const s = String(f || '');
    if (/^month/i.test(s)) return `${s} · 12`;
    if (/^quarter/i.test(s)) return `${s} · 4`;
    if (/^semi/i.test(s)) return `${s} · 2`;
    return `${s} · 1`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl bg-white shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-800">New work plan</h3>
            <p className="text-sm text-slate-500">Carry the same activities into the new year, or tick only the ones to carry. Add new ones afterwards with Add activity or Build annual work plan.</p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none" aria-label="Close">×</button>
        </div>

        <div className="px-6 py-4 flex flex-wrap items-end gap-4 border-b border-slate-100">
          <label className="text-sm text-slate-700">
            <span className="block text-xs font-medium text-slate-500 mb-1">Plan year</span>
            <input type="number" min="2020" max="2100" value={year} onChange={(e) => setYear(e.target.value)}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm w-28" />
          </label>
          <label className="text-sm text-slate-700">
            <span className="block text-xs font-medium text-slate-500 mb-1">Carry activities from</span>
            <select value={sourceId} onChange={(e) => setSourceId(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm min-w-[180px]">
              <option value="">— Start empty —</option>
              {sorted.map((p) => <option key={p.id} value={p.id}>{p.plan_year} — {p.status}</option>)}
            </select>
          </label>
          {sourceId && total > 0 && (
            <div className="text-xs text-slate-500 flex items-center gap-3">
              <span>{picked.size} of {total} activities selected</span>
              <button type="button" onClick={all} className="underline hover:text-slate-800">All</button>
              <button type="button" onClick={none} className="underline hover:text-slate-800">None</button>
            </div>
          )}
        </div>

        <div className="px-6 py-3 overflow-y-auto flex-1">
          {loading && <p className="text-sm text-slate-500">Reading the activities of {source?.plan_year}…</p>}
          {!loading && sourceId && total === 0 && <p className="text-sm text-slate-500">That year holds no activities to carry.</p>}
          {!loading && cands.series.length > 0 && (
            <div className="mb-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Synced measures ({cands.series.length})</div>
              <ul className="divide-y divide-slate-100 border border-slate-100 rounded-xl">
                {cands.series.map((s) => {
                  const key = `s:${s.id}`;
                  return (
                    <li key={key} className="flex items-center gap-3 px-3 py-2 text-sm">
                      <input type="checkbox" checked={picked.has(key)} onChange={() => toggle(key)} aria-label={`Carry ${s.component || s.measure_label}`} />
                      <span className="flex-1 text-slate-800">{s.component || s.measure_label}</span>
                      <span className="text-xs text-slate-500">{s.entity_name || 'Internal'}</span>
                      <span className="text-xs text-slate-500 w-32 text-right">{cadenceWord(s.cadence)}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {!loading && cands.manual.length > 0 && (
            <div className="mb-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Hand-entered activities ({cands.manual.length})</div>
              <ul className="divide-y divide-slate-100 border border-slate-100 rounded-xl">
                {cands.manual.map((m) => {
                  const key = `m:${m.id}`;
                  return (
                    <li key={key} className="flex items-center gap-3 px-3 py-2 text-sm">
                      <input type="checkbox" checked={picked.has(key)} onChange={() => toggle(key)} aria-label={`Carry ${m.component}`} />
                      <span className="flex-1 text-slate-800">
                        {m.component}
                        {m.activity_description && <span className="block text-xs text-slate-500 truncate max-w-md">{m.activity_description}</span>}
                      </span>
                      <span className="text-xs text-slate-500">{m.entity_name || 'Internal'}</span>
                      <span className="text-xs text-slate-500 w-32 text-right">{freqWord(m.activity_frequency)}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
          {sameYear && <p className="text-sm text-amber-700 mt-2">Pick a different year than {source.plan_year} to carry activities into.</p>}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex flex-wrap items-center justify-end gap-3">
          <button type="button" onClick={() => submit(false)} disabled={busy}
            className="px-4 py-2 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:border-slate-300 disabled:opacity-50">
            Create empty {year} plan
          </button>
          <button type="button" onClick={() => submit(true)} disabled={busy || !sourceId || sameYear || picked.size === 0}
            className="px-5 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm font-medium shadow-sm disabled:opacity-50">
            {busy ? 'Creating…' : `Create ${year} with ${picked.size === total ? 'all' : picked.size} activit${picked.size === 1 ? 'y' : 'ies'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
